import { MuxAutoFilter, MuxTabFilter } from "@/Icons";
import { useContext, useEffect, useState } from "react";
import { PtTree } from "@/shared-components";
import {
  FavoritesProvider,
  FiltersContext,
  useFavoritesContext,
} from "@/contexts";
import { FilterContextMenu } from "../";
import { FiltersService, SearchService, TreeService } from "@/services";
import { DEFAULT_FILTER_KEY } from "@/constants";
import { getTranslation, hasPermission } from "@/utils";
import { AddMenuItem } from "@/shared-components/PtTree/components/AddNewListItem/AddNewListItem";
import { ISearchResult } from "@/interfaces";
import { FavoriteItem } from "@/types";

interface IFilterListProps {
  notifyFilterChanged: (node: any, isUserAction?: boolean) => void;
  children?: React.ReactNode;
  maxNumberOfFavorites: number;
}

export const FilterList = ({
  notifyFilterChanged,
  maxNumberOfFavorites,
}: IFilterListProps) => {
  const store = useContext(FiltersContext);
  const {
    loading,
    error,
    scope,
    criteria,
    items,
    settings,
    selectedItem,
    setCriteria,
    setSelectedItem,
    handleNodeExpand,
    setLoading,
    handleServerSideSearch,
  } = store!;

  const hasEditPermissions =
    hasPermission("custom_views_all") ||
    settings?.moduleEditPermissions?.some((permission) =>
      hasPermission(permission),
    );
  const { saveFavorites, favorites, setActiveFavorite } = useFavoritesContext();

  const [openNodes, setOpenedNodes] = useState<string[]>([]);

  useEffect(() => {
    if (items.length) {
      setOpenedNodes(TreeService.getOpenedNodes(scope));
    }
  }, [items.length, selectedItem?.id]);

  useEffect(() => {
    if (selectedItem?.id) {
      setActiveFavorite(selectedItem.id);
    }
  }, [selectedItem?.id]);

  const handleTreeItemClick = (
    node: any | undefined,
    isUserAction: boolean = true,
  ) => {
    if (node?.id === DEFAULT_FILTER_KEY) {
      setActiveFavorite(null);
    }
    TreeService.setActiveNode(scope, node);
    setSelectedItem(node?.id !== DEFAULT_FILTER_KEY ? node : undefined);
    notifyFilterChanged(node, isUserAction);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<{ id: string | number }>).detail?.id;
      if (id == null) return;
      const node =
        id === DEFAULT_FILTER_KEY
          ? { id: DEFAULT_FILTER_KEY }
          : SearchService.findFilterItem(items, String(id));
      if (node) {
        handleTreeItemClick(node, false);
      } else {
        console.warn(`pt-select-filter: no filter with id "${id}" in tree`);
      }
    };
    window.addEventListener("pt-select-filter", handler);
    return () => window.removeEventListener("pt-select-filter", handler);
  }, [items]);

  const handleStateFromUrl = async (
    favorite: FavoriteItem | null = null,
  ): Promise<void> => {
    const favoriteId = favorite?.id || "";

    const currFilter = SearchService.findFilterItem(items, favoriteId);
    if (currFilter) {
      setSelectedItem(currFilter);
      notifyFilterChanged(currFilter, false);
    }
  };

  const handleAddItem = (filterType?: "manual" | "auto") => {
    const type = typeof filterType === "string" ? filterType : undefined;
    mixpanel.track("filters-tree", {
      Action: "add-filter",
      ...(type ? { "Filter Type": filterType } : {}),
    });
    if (store?.createView) {
      store.createView(null, type);
    } else {
      const url = `/projects/${scope.projectId}/custom_views/new?view_type=${scope.module}`;
      window.location.href = url;
    }
  };

  const addItemMenuItems: AddMenuItem[] | undefined = store?.createView
    ? [
        {
          text: getTranslation(
            "filters.add.auto-filter",
            "Create an Auto Filter",
          ),
          icon: <MuxAutoFilter />,
          onClick: () => handleAddItem("auto"),
        },
        {
          text: getTranslation(
            "filters.add.manual-filter",
            "Create Manual Filter",
          ),
          icon: <MuxTabFilter />,
          onClick: () => handleAddItem("manual"),
        },
      ]
    : undefined;

  const handleNodeRename = async (
    filter_id: string,
    value: string,
  ): Promise<void> => {
    await FiltersService.rename(scope.projectId, filter_id, value, items);
    await saveFavorites(favorites);
  };

  const handleSearch = async (
    criteria: string,
  ): Promise<ISearchResult | undefined> => {
    setLoading(true);
    const res = await handleServerSideSearch(criteria);

    setLoading(false);
    // ToDo: fix typescript error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return res || undefined;
  };

  return (
    <FavoritesProvider
      scope={scope}
      tree={items || []}
      maxNumberOfFavorites={maxNumberOfFavorites}
    >
      <PtTree
        data={items || []}
        scope={scope}
        settings={settings}
        initOpenNodes={openNodes || []}
        contextMenu={({ data, setIsEditable }: any) => (
          <FilterContextMenu
            setIsEditable={setIsEditable}
            items={items}
            key={data.key}
            data={data}
            hasEditPermissions={hasEditPermissions}
          />
        )}
        handleStateFromUrl={handleStateFromUrl}
        criteria={criteria}
        setCriteria={setCriteria}
        selectedItem={selectedItem}
        loading={loading}
        error={error}
        hasEditPermissions={hasEditPermissions}
        notifyTreeNodeOpen={handleNodeExpand}
        handleTreeItemClick={handleTreeItemClick}
        handleAddItem={handleAddItem}
        addItemMenuItems={addItemMenuItems}
        handleNodeRename={handleNodeRename}
        handleSearch={handleSearch}
      />
    </FavoritesProvider>
  );
};
